/**
 * IconGrid Component
 *
 * Virtualized grid for displaying icons in the IconPicker.
 * Uses @tanstack/react-virtual for efficient rendering of large icon sets.
 *
 * Features:
 * - Virtual scrolling for performance with 100+ icons
 * - Keyboard navigation with arrow keys
 * - ARIA radiogroup pattern for accessibility
 * - Focus management across virtual scroll
 */

import { useVirtualizer } from '@tanstack/react-virtual';
import { Package } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { NO_DATA_ICON_CLASS } from '@/components/legacy-ui/utils/createNoDataConfig';
import { useVirtualFocus } from '@/sections/attacks/attackPaths/components/icons/hooks';
import type { IconDefinition } from '@/sections/attacks/attackPaths/components/icons/registry';
import { isIconSelected } from '@/sections/attacks/attackPaths/components/icons/utils/iconSelectionUtils';
import { SearchEmptyState } from '@/sections/attacks/attackPaths/components/wizards/components';
import { cn } from '@/utils/classname';

import { IconCard } from './IconCard';

interface IconGridProps {
  /** Icons to display in the grid */
  icons: IconDefinition[];
  /** Currently selected icon id */
  selectedIcon: string | undefined;
  /** Callback when an icon is selected */
  onSelect: (icon: IconDefinition) => void;
  /** Category id for ARIA attributes */
  categoryId: string;
  /** Current search term (for differentiated empty state messaging) */
  searchTerm?: string;
  /** Callback to clear search (for search empty state action button) */
  onClearSearch?: () => void;
}

/** Number of columns in the grid */
const COLUMNS = 5;

/** Height of each row in pixels */
const ROW_HEIGHT = 96;

/**
 * Virtualized icon grid with keyboard navigation.
 *
 * Renders only visible rows for performance with large icon sets.
 * Supports arrow key navigation with automatic scroll-into-view.
 *
 * @example
 * ```tsx
 * <IconGrid
 *   icons={filteredIcons}
 *   selectedIcon={selectedIcon}
 *   onSelect={handleSelect}
 *   categoryId="all"
 * />
 * ```
 */
export function IconGrid({
  icons,
  selectedIcon,
  onSelect,
  categoryId,
  searchTerm,
  onClearSearch,
}: IconGridProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowCount = Math.ceil(icons.length / COLUMNS);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 3,
  });

  const { focusedIndex, setFocusedIndex, handleKeyDown } = useVirtualFocus({
    totalItems: icons.length,
    columnsPerRow: COLUMNS,
    virtualizer,
  });

  // Reset focus index when category changes to prevent focus ring from
  // appearing on wrong icons at the same position in different categories
  useEffect(() => {
    setFocusedIndex(-1);
  }, [categoryId, setFocusedIndex]);

  const handleSelect = useCallback(
    (icon: IconDefinition, index: number) => {
      setFocusedIndex(index);
      onSelect(icon);
    },
    [onSelect, setFocusedIndex]
  );

  const handleKeyDownWithSelect = useCallback(
    (e: React.KeyboardEvent) => {
      handleKeyDown(e, index => {
        const icon = icons[index];
        if (icon) handleSelect(icon, index);
      });
    },
    [handleKeyDown, icons, handleSelect]
  );

  // Empty state - differentiate between search empty and category empty
  if (icons.length === 0) {
    const hasSearchTerm = Boolean(searchTerm?.trim());

    // Search empty state - use centralized component
    if (hasSearchTerm && onClearSearch && searchTerm) {
      return (
        <SearchEmptyState
          searchTerm={searchTerm}
          icon={Package}
          onClearSearch={onClearSearch}
          entityName="icons"
          height="h-[304px]"
          testIdPrefix="icon-grid-empty"
        />
      );
    }

    // Category empty state (no icons in category yet)
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        data-testid="icon-grid-empty"
        role="status"
        aria-live="polite"
        tabIndex={0}
      >
        <Package
          className={cn(NO_DATA_ICON_CLASS, 'mb-3 size-12')}
          strokeWidth={1.5}
        />
        <p className="text-chariot-text-secondary text-sm font-medium">
          No icons in this category yet
        </p>
        <p className="text-chariot-text-tertiary mt-1 text-xs">
          Icons for this category will be added in a future update
        </p>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      role="tabpanel"
      id={`panel-${categoryId}`}
      aria-labelledby={`tab-${categoryId}`}
      className="border-chariot-stroke bg-chariot-background-primary h-[304px] overflow-auto rounded-lg border p-2"
      onKeyDown={handleKeyDownWithSelect}
      data-testid="icon-grid"
    >
      <div
        role="radiogroup"
        aria-label="Available icons"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map(virtualRow => {
          const rowStartIndex = virtualRow.index * COLUMNS;
          const rowIcons = icons.slice(rowStartIndex, rowStartIndex + COLUMNS);

          return (
            <div
              key={virtualRow.key}
              className="absolute top-0 left-0 grid w-full grid-cols-5 gap-2"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {rowIcons.map((icon, colIndex) => {
                const absoluteIndex = rowStartIndex + colIndex;
                return (
                  <IconCard
                    key={icon.id}
                    icon={icon}
                    isSelected={isIconSelected(
                      selectedIcon,
                      icon.id,
                      categoryId
                    )}
                    isFocused={absoluteIndex === focusedIndex}
                    onSelect={icon => handleSelect(icon, absoluteIndex)}
                    tabIndex={absoluteIndex === focusedIndex ? 0 : -1}
                    categoryContext={categoryId}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
